/**
 * The @interface CatalogItem is a simplified representation of the complete ITSE Hardware catalog response.
 * It includes only the essential attributes required by the application, while the actual response contains
 * additional properties not defined in this interface.
 *
 * The following properties are available in the full response based on the ITSE Hardware schema:
 * @property {string} mainCategory - The main category of item (part of taxonomy id, max 100 chars)
 * @property {string} subCategory - The sub category of item (part of taxonomy id, max 100 chars)
 * @property {string} manufacturer - The manufacturer or vendor name (part of taxonomy id, max 100 chars)
 * @property {string} description - The full description of the item (max 10000 chars)
 * @property {{amount: number, currency: string}} cost - The cost and currency of the item
 * @property {number} etaDays - Estimated time of arrival for provisioning (non-negative integer)
 * @property {boolean} isActive - Whether the item is currently active
 * @property {string[]} rules - Rules defining item availability (json-rules-engine format)
 * @property {"Standard" | "NonStandard"} type - Item type classification
 * @property {string[]} approvalsRulesTemplate - Additional approval workflow rules
 * @property {{on: string, via: string, by: string}} createdStamp - Creation timestamp and metadata
 * @property {{on: string, via: string, by: string}[]} updatedStamps - Update timestamps and metadata
 */

/**
 * Interface for the response of a catalog item.
 *
 * @interface
 * @property {string} taxonomyId - The taxonomy classification ID
 * @property {string} name - The name of the catalog item
 * @property {string} sku - The stock keeping unit identifier
 * @property {string} description - The description of the catalog
 * @property {string[]} categories - The categories of the catalog item
 *
 */
export interface CatalogItem {
    taxonomyId: string;
    name: string;
    sku: string;
    description: string;
    categories?: string[];
}

/**
 * The unique identifier for a catalog item based on the combination of taxonomy parts.
 * @constant {string}
 */
export const CATALOG_ITEM_UNIQUE_IDENTIFIER: string = "taxonomyId";

/**
 * Interface for the pagination response of a catalog request.
 * @interface
 * @property {number} limit - The maximum number of items to return.
 * @property {number} offset - The number of items to skip before returning results.
 * @property {number} total - The total number of items available.
 */
export interface paginationResponse {
    limit: number;
    offset: number;
    total: number;
}

/**
 * Interface for the service response that contains an array of catalog request responses.
 * @interface
 * @property {CatalogRequestResponse[]} data - The array of catalog request responses.
 * @property {paginationResponse} pagination - The pagination response for the catalog request.
 */
export interface CatalogItemServiceResponse {
    data: CatalogItem[];
    pagination: paginationResponse;
}

/**
 * Interface representing a catalog category from the itsehamaux catalogical domain
 *
 * @interface
 * @property {string} taxonomyId - The unique taxonomy identifier for the category
 * @property {boolean} selectable - Determines if the category is selectable as an item category
 * @property {string} name - The full name of the category
 * @property {string} description - The category description
 * @property {string} parentTaxonomyId - The taxonomy ID of the parent category
 * @property {string} shortName - The abbreviated name of the category
 * @property {string} type - The type classification of the category
 */
export interface CatalogCategory {
    taxonomyId: string;
    selectable: boolean;
    name: string;
    description: string;
    parentTaxonomyId: string;
    shortName: string;
    type: string;
}

/**
 * Interface for the service response that contains an array of catalog categories.
 *
 * @interface
 * @property {CatalogCategory[]} data - The array of catalog categories
 * @property {paginationResponse} pagination - The pagination response for the category request
 */
export interface CatalogCategoryServiceResponse {
    data: CatalogCategory[];
    pagination: paginationResponse;
}
